import { useState, useEffect } from 'react';
import { Modal, Table, Button, message } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { reportsService, type DrillDownInspection } from '@/services/reports.service';
import type { DrillDownFilters } from '../useDrillDown';
import { INSPECTION_ITEM_STATUS_MAP } from '@/shared/constants/statusMaps';
import { InspectionItemStatus } from '@/shared/enums/InspectionStatus';
import { DetailInspection } from '@/components/DetailInspection';
import { StatusBadge } from '@/components/StatusBadge';

interface DrillDownModalProps {
  visible: boolean;
  title: string;
  filters: DrillDownFilters;
  onClose: () => void;
}

export function DrillDownModal({ visible, title, filters, onClose }: DrillDownModalProps) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<DrillDownInspection[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedInspectionId, setSelectedInspectionId] = useState<string | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      loadData(currentPage, pageSize);
    }
  }, [visible, filters, currentPage, pageSize]);

  const loadData = async (page: number, size: number) => {
    try {
      setLoading(true);
      const response = await reportsService.getDrillDown({
        ...filters,
        pageNumber: page,
        pageSize: size,
      });
      setData(response.items);
      setTotal(response.totalCount);
    } catch {
      message.error('Erro ao carregar detalhes das vistorias');
    } finally {
      setLoading(false);
    }
  };

  const handleViewInspection = (record: DrillDownInspection) => {
    setSelectedInspectionId(record.inspectionRequestId);
    setSelectedItemId(record.id);
    setIsDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedInspectionId(null);
    setSelectedItemId(null);
    loadData(currentPage, pageSize);
  };

  const handleTableChange = (pagination: any) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const getStatusInfo = (statusName: string) => {
    
    const statusKey = Object.keys(InspectionItemStatus).find(
      key => key === statusName
    ) as keyof typeof InspectionItemStatus | undefined;

    if (statusKey && INSPECTION_ITEM_STATUS_MAP[InspectionItemStatus[statusKey]]) {
      const statusInfo = INSPECTION_ITEM_STATUS_MAP[InspectionItemStatus[statusKey]];
      return {
        label: statusInfo.label,
        color: statusInfo.color,
        badge: statusInfo.badge
      };
    }


    return {
      label: statusName,
      color: '#d9d9d9',
      badge: statusName
    };
  };

  const columns: ColumnsType<DrillDownInspection> = [
    {
      title: 'Código',
      dataIndex: 'code',
      key: 'code',
      width: 150,
      fixed: 'left',
      render: (text) => <span style={{ fontWeight: 500 }}>{text}</span>,
    },
    {
      title: 'Tipo',
      dataIndex: 'type',
      key: 'type',
      width: 150,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 250,
      render: (status: string) => {
        const statusInfo = getStatusInfo(status);
        return <StatusBadge label={statusInfo.label} color={statusInfo.color} />;
      },
    },
    {
      title: 'Localização',
      key: 'location',
      width: 200,
      render: (_, record) => `${record.city}-${record.state}`,
    },
    {
      title: 'Inspetor',
      dataIndex: 'inspectorName',
      key: 'inspectorName',
      width: 150,
      render: (name) => name || '-',
    },
    {
      title: 'Prestadora',
      dataIndex: 'providerName',
      key: 'providerName',
      width: 150,
      render: (name) => name || '-',
    },
    {
      title: 'Data Criação',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date) => new Date(date).toLocaleDateString('pt-BR'),
    },
    {
      title: 'Data Agendamento',
      dataIndex: 'scheduledDate',
      key: 'scheduledDate',
      width: 130,
      render: (date) => date ? new Date(date).toLocaleDateString('pt-BR') : '-',
    },
    {
      title: 'Data Conclusão',
      dataIndex: 'completedDate',
      key: 'completedDate',
      width: 120,
      render: (date) => date ? new Date(date).toLocaleDateString('pt-BR') : '-',
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewInspection(record)}
        >
          Ver
        </Button>
      ),
    },
  ];

  return (
    <>
      <Modal
        title={title}
        open={visible}
        onCancel={onClose}
        footer={null}
        width={1200}
        style={{ top: 20 }}
      >
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} vistorias`,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          onChange={handleTableChange}
          scroll={{ x: 1400 }}
          size="small"
        />
      </Modal>

      <DetailInspection
        open={isDetailModalOpen}
        inspectionId={selectedInspectionId}
        itemId={selectedItemId}
        onClose={handleCloseDetailModal}
        onUpdate={handleCloseDetailModal}
      />
    </>
  );
}
